import { getApiUrl } from "../config/api";

/** Meta Dataset ID — override with REACT_APP_META_PIXEL_ID in .env if needed */
export const META_PIXEL_ID =
  process.env.REACT_APP_META_PIXEL_ID || "1252672400019769";

const CURRENCY = "MAD";

export type MetaContentItem = {
  content_id: string;
  content_name?: string;
  content_type?: string;
  quantity?: number;
  price?: number;
};

type FbqFn = (
  command: string,
  eventOrPixelId?: string,
  params?: Record<string, unknown>,
  options?: { eventID?: string }
) => void;

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

let metaPixelInitialized = false;

function getCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

/** Meta click / cookie ids for Conversions API matching */
export function getMetaAttribution(): { fbp: string; fbc: string } {
  if (typeof window === "undefined") {
    return { fbp: "", fbc: "" };
  }
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get("fbclid") || "";
  let fbc = getCookie("_fbc") || "";
  if (!fbc && fbclid) {
    fbc = `fb.1.${Date.now()}.${fbclid}`;
  }
  return {
    fbp: getCookie("_fbp") || "",
    fbc,
  };
}

function metaEventsApiUrl(): string {
  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    if (origin === "https://www.kissariti.ma" || origin === "https://kissariti.ma") {
      return "/api/meta_events.php";
    }
  }
  return getApiUrl("META_EVENTS");
}

function getFbq(): FbqFn | null {
  if (typeof window === "undefined" || !window.fbq) return null;
  return window.fbq;
}

/** Meta Pixel base script is loaded from public/index.html */
export function metaInitPixel(): void {
  if (typeof window === "undefined" || metaPixelInitialized) return;
  metaPixelInitialized = true;
}

export function metaPageView(): void {
  if (!getFbq()) return;
  try {
    getFbq()?.("track", "PageView");
  } catch {
    /* optional */
  }
}

/** Stable id for Pixel + Conversions API deduplication */
export function metaEventId(suffix: string, eventName: string): string {
  return `${eventName}_${suffix}`.replace(/\s+/g, "_");
}

export function metaGenerateEventId(
  eventName: string,
  suffix?: string
): string {
  const unique =
    suffix ||
    `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  return metaEventId(unique, eventName);
}

/** Meta Pixel `contents`: { id, quantity, item_price? } */
function toMetaPixelContents(items: MetaContentItem[]) {
  return items.map((item) => ({
    id: String(item.content_id),
    quantity: item.quantity ?? 1,
    ...(item.price != null ? { item_price: Number(item.price) } : {}),
  }));
}

function toContentsArray(items: MetaContentItem[]) {
  return items.map((item) => ({
    content_id: String(item.content_id),
    content_type: item.content_type || "product",
    content_name: item.content_name || "",
    ...(item.quantity != null ? { quantity: item.quantity } : {}),
    ...(item.price != null ? { price: item.price } : {}),
  }));
}

/**
 * Standard event payload per Meta specs:
 * value, currency, content_ids, content_type, contents, num_items, content_name
 */
function buildStandardEventPayload(
  value: number,
  contents: MetaContentItem[],
  contentName?: string
) {
  const contentIds = contents.map((c) => String(c.content_id));
  const numItems = contents.reduce(
    (sum, item) => sum + (item.quantity ?? 1),
    0
  );
  return {
    value: Number(value) || 0,
    currency: CURRENCY,
    content_type: "product",
    content_ids: contentIds,
    contents: toMetaPixelContents(contents),
    ...(numItems > 0 ? { num_items: numItems } : {}),
    ...(contentName ? { content_name: contentName } : {}),
    /** Internal — forwarded to Conversions API only */
    _serverContents: contents,
  };
}

type ServerEventOpts = {
  event: string;
  eventId?: string;
  value?: number;
  contents?: MetaContentItem[];
  email?: string;
  phone?: string;
  external_id?: string;
};

function sendMetaServerEvent(opts: ServerEventOpts): void {
  if (typeof window === "undefined") return;
  const { fbp, fbc } = getMetaAttribution();
  const body: Record<string, unknown> = {
    event: opts.event,
    event_id: opts.eventId,
    event_time: Math.floor(Date.now() / 1000),
    value: opts.value ?? 0,
    currency: CURRENCY,
    contents: opts.contents ? toContentsArray(opts.contents) : [],
    url: window.location.href.split("#")[0],
    fbp: fbp || undefined,
    fbc: fbc || undefined,
  };
  if (opts.email?.trim()) body.email = opts.email.trim();
  if (opts.phone?.trim()) body.phone = opts.phone.trim();
  if (opts.external_id?.trim()) body.external_id = opts.external_id.trim();

  fetch(metaEventsApiUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {});
}

function metaTrackEvent(
  event: string,
  properties: Record<string, unknown>,
  eventId?: string,
  serverOpts?: Omit<ServerEventOpts, "event" | "eventId">
): void {
  metaInitPixel();

  const serverContents =
    (properties._serverContents as MetaContentItem[]) ||
    serverOpts?.contents ||
    [];

  const browserPayload: Record<string, unknown> = {
    value: properties.value,
    currency: properties.currency,
    content_ids: properties.content_ids,
    content_type: properties.content_type,
    contents: properties.contents,
    num_items: properties.num_items,
    content_name: properties.content_name,
  };
  Object.keys(browserPayload).forEach((key) => {
    if (browserPayload[key] === undefined) {
      delete browserPayload[key];
    }
  });

  try {
    const fbq = getFbq();
    if (fbq) {
      if (eventId) {
        fbq("track", event, browserPayload, { eventID: eventId });
      } else {
        fbq("track", event, browserPayload);
      }
    }
  } catch {
    /* optional */
  }

  sendMetaServerEvent({
    event,
    eventId,
    value: Number(properties.value) || 0,
    contents: serverContents,
    email: serverOpts?.email,
    phone: serverOpts?.phone,
    external_id: serverOpts?.external_id,
  });
}

/** ViewContent — product / landing page */
export function metaViewContent(opts: {
  contentId: string;
  contentName: string;
  value?: number;
}): void {
  const contents: MetaContentItem[] = [
    {
      content_id: opts.contentId,
      content_name: opts.contentName,
      content_type: "product",
      quantity: 1,
      price: opts.value,
    },
  ];
  const eventId = metaEventId(opts.contentId, "ViewContent");
  metaTrackEvent(
    "ViewContent",
    buildStandardEventPayload(opts.value ?? 0, contents, opts.contentName),
    eventId
  );
}

/** AddToCart — item added to basket */
export function metaAddToCart(opts: {
  contentId: string;
  contentName: string;
  quantity?: number;
  value?: number;
}): void {
  const qty = opts.quantity ?? 1;
  const unitPrice =
    opts.value != null && qty ? opts.value / qty : undefined;
  const contents: MetaContentItem[] = [
    {
      content_id: opts.contentId,
      content_name: opts.contentName,
      content_type: "product",
      quantity: qty,
      price: unitPrice,
    },
  ];
  const eventId = metaGenerateEventId("AddToCart", opts.contentId);
  metaTrackEvent(
    "AddToCart",
    buildStandardEventPayload(opts.value ?? 0, contents),
    eventId
  );
}

/** InitiateCheckout — checkout page opened */
export function metaInitiateCheckout(opts: {
  value: number;
  contents: MetaContentItem[];
  eventId?: string;
}): void {
  const eventId = opts.eventId || metaGenerateEventId("InitiateCheckout");
  metaTrackEvent(
    "InitiateCheckout",
    buildStandardEventPayload(opts.value, opts.contents),
    eventId
  );
}

/** AddPaymentInfo — payment method selected at checkout */
export function metaAddPaymentInfo(opts: {
  value: number;
  contents: MetaContentItem[];
}): void {
  const eventId = metaGenerateEventId("AddPaymentInfo");
  metaTrackEvent(
    "AddPaymentInfo",
    buildStandardEventPayload(opts.value, opts.contents),
    eventId
  );
}

const CONVERSION_STORAGE_PREFIX = "kissariti_meta_order_";

/** Purchase — order confirmed (requires value + currency) */
export function metaTrackOrderPlaced(opts: {
  orderId: string;
  value: number;
  contents: MetaContentItem[];
  email?: string;
  phone?: string;
  externalId?: string;
}): void {
  if (!opts.orderId) return;

  const dedupeKey = `${CONVERSION_STORAGE_PREFIX}${opts.orderId}`;
  try {
    if (
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(dedupeKey)
    ) {
      return;
    }
    sessionStorage.setItem(dedupeKey, "1");
  } catch {
    /* continue */
  }

  const purchaseEventId = metaEventId(opts.orderId, "Purchase");
  const serverUser = {
    email: opts.email,
    phone: opts.phone,
    external_id: opts.externalId || opts.orderId,
  };

  metaTrackEvent(
    "Purchase",
    buildStandardEventPayload(opts.value, opts.contents),
    purchaseEventId,
    serverUser
  );
}
